/**
 * Phase 87av — Renderer Tiptap (ProseMirror JSON) → React-PDF.
 *
 * Walks the Tiptap document tree and emits <Text>/<View> nodes with marks
 * applied as styles. Supported nodes:
 *   - doc, paragraph
 *   - heading (level 1-3)
 *   - bulletList, orderedList, listItem
 *   - hardBreak
 *   - blockquote
 * Supported marks:
 *   - bold, italic, underline, link, strike
 *
 * Any unknown node/mark is rendered as plain text (graceful degradation).
 */
import React from "react";
import { Text, View, Link, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  paragraph: { fontSize: 10, lineHeight: 1.55, marginBottom: 6 },
  heading1: { fontSize: 16, fontFamily: "Helvetica-Bold", marginTop: 10, marginBottom: 6, color: "#1F4E3D" },
  heading2: { fontSize: 13, fontFamily: "Helvetica-Bold", marginTop: 8, marginBottom: 5, color: "#1F4E3D" },
  heading3: { fontSize: 11, fontFamily: "Helvetica-Bold", marginTop: 6, marginBottom: 4, color: "#8C6B2F" },
  listItem: { flexDirection: "row", marginBottom: 3, paddingLeft: 6 },
  bullet: { fontSize: 10, marginRight: 6, color: "#8C6B2F" },
  ordered: { fontSize: 10, marginRight: 6, color: "#8C6B2F", minWidth: 14 },
  listItemContent: { flex: 1, fontSize: 10, lineHeight: 1.55 },
  blockquote: { marginBottom: 6, paddingLeft: 8, borderLeft: "2pt solid #C1A063", fontSize: 10, fontFamily: "Helvetica-Oblique", color: "#575A57", lineHeight: 1.55 },
  link: { color: "#1F4E3D", textDecoration: "underline" },
});

/** Render a Tiptap inline node (text + marks) inside a <Text> wrapper. */
function renderInline(node, key) {
  if (node.type === "hardBreak") {
    return "\n";
  }
  if (node.type !== "text") {
    // Unknown inline → fallback to children text
    if (Array.isArray(node.content)) {
      return node.content.map((c, i) => renderInline(c, `${key}-${i}`));
    }
    return "";
  }
  const marks = node.marks || [];
  let element = node.text;
  let style = {};
  let isLink = null;
  for (const m of marks) {
    if (m.type === "bold") style.fontFamily = style.fontFamily === "Helvetica-Oblique" ? "Helvetica-BoldOblique" : "Helvetica-Bold";
    else if (m.type === "italic") {
      style.fontFamily = style.fontFamily === "Helvetica-Bold" ? "Helvetica-BoldOblique" : "Helvetica-Oblique";
    }
    else if (m.type === "underline") style.textDecoration = (style.textDecoration ? style.textDecoration + " " : "") + "underline";
    else if (m.type === "strike") style.textDecoration = (style.textDecoration ? style.textDecoration + " " : "") + "line-through";
    else if (m.type === "link") isLink = m.attrs?.href;
  }
  if (isLink) {
    return <Link key={key} src={isLink} style={{ ...styles.link, ...style }}>{element}</Link>;
  }
  return <Text key={key} style={style}>{element}</Text>;
}

/** Render a list of inline nodes. */
function renderInlines(content, keyPrefix) {
  if (!Array.isArray(content)) return null;
  return content.map((n, i) => renderInline(n, `${keyPrefix}-i${i}`));
}

/** Render a Tiptap block node as a React-PDF View/Text. */
function renderBlock(node, key, depth = 0) {
  switch (node.type) {
    case "paragraph": {
      const children = renderInlines(node.content || [], key);
      // Vide ? on rend un saut de ligne pour préserver la mise en forme
      const hasContent = (node.content || []).some((c) => c.type === "text" && (c.text || "").length > 0);
      return (
        <Text key={key} style={styles.paragraph}>
          {hasContent ? children : " "}
        </Text>
      );
    }
    case "heading": {
      const level = node.attrs?.level || 1;
      const headingStyle = level === 1 ? styles.heading1 : level === 2 ? styles.heading2 : styles.heading3;
      return <Text key={key} style={headingStyle}>{renderInlines(node.content || [], key)}</Text>;
    }
    case "bulletList": {
      return (
        <View key={key}>
          {(node.content || []).map((item, i) =>
            <View key={`${key}-li${i}`} style={styles.listItem}>
              <Text style={styles.bullet}>•</Text>
              <View style={styles.listItemContent}>
                {(item.content || []).map((sub, j) => renderBlock(sub, `${key}-li${i}-s${j}`, depth + 1))}
              </View>
            </View>
          )}
        </View>
      );
    }
    case "orderedList": {
      return (
        <View key={key}>
          {(node.content || []).map((item, i) =>
            <View key={`${key}-li${i}`} style={styles.listItem}>
              <Text style={styles.ordered}>{i + 1}.</Text>
              <View style={styles.listItemContent}>
                {(item.content || []).map((sub, j) => renderBlock(sub, `${key}-li${i}-s${j}`, depth + 1))}
              </View>
            </View>
          )}
        </View>
      );
    }
    case "blockquote": {
      return (
        <View key={key} style={styles.blockquote}>
          {(node.content || []).map((sub, j) => renderBlock(sub, `${key}-q${j}`, depth + 1))}
        </View>
      );
    }
    case "listItem": {
      // Should be handled by parent list; fallback
      return (
        <View key={key} style={styles.listItem}>
          <Text style={styles.bullet}>•</Text>
          <View style={styles.listItemContent}>
            {(node.content || []).map((sub, j) => renderBlock(sub, `${key}-li-s${j}`, depth + 1))}
          </View>
        </View>
      );
    }
    case "horizontalRule": {
      return <View key={key} style={{ borderTop: "0.5pt solid #C1A063", marginVertical: 8 }} />;
    }
    default:
      // Unknown block — render its content if any
      if (Array.isArray(node.content)) {
        return (
          <View key={key}>
            {node.content.map((sub, j) => renderBlock(sub, `${key}-${j}`, depth))}
          </View>
        );
      }
      return null;
  }
}

/** Main export — render a Tiptap doc JSON to React-PDF nodes. */
export function renderTiptap(docJson) {
  if (!docJson || typeof docJson !== "object") {
    return <Text style={styles.paragraph}>(corps vide)</Text>;
  }
  // Tiptap root: { type: "doc", content: [...] }
  const content = docJson.content || (Array.isArray(docJson) ? docJson : []);
  if (!content.length) {
    return <Text style={styles.paragraph}>(corps vide)</Text>;
  }
  return content.map((n, i) => renderBlock(n, `b${i}`));
}

export default renderTiptap;
